import { Link, useSearchParams } from 'react-router-dom';
import { useApi } from '../../lib/useApi.js';
import { useUrlPagination } from '../../lib/usePagination.js';
import {
  PageHeader,
  ErrorState,
  HealthIndicator,
  PageViewerControls,
  KpiHeroBand,
  StatusDonutMini,
  KpiBandSkeleton,
  TableSkeleton,
  BrandedEmptyState,
  StaggerReveal,
} from '../../components/index.js';
import { format } from '@dha-nexus/shared';
import './portfolios.redesign.css';

function toNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// At-risk-aware tone for a per-card metric value.
function deliveryTone({ atRisk, active }) {
  if (atRisk <= 0) return active > 0 ? 'is-sage' : '';
  if (active > 0 && atRisk / active >= 0.34) return 'is-brick';
  return 'is-amber';
}

export function PortfolioListPage() {
  const [params, setParams] = useSearchParams();
  const pager = useUrlPagination(params, setParams, { defaultPageSize: 8 });
  const r = useApi('/portfolios', { query: { limit: pager.pageSize, offset: pager.offset } });

  if (r.loading)
    return (
      <>
        <PageHeader
          eyebrow="Executive"
          title="Portfolios"
          subtitle="Mission-aligned investment portfolios across DHA."
        />
        <KpiBandSkeleton count={4} />
        <div style={{ marginTop: 16 }}>
          <TableSkeleton rows={6} />
        </div>
      </>
    );
  if (r.error) return <ErrorState error={r.error} />;

  const items = r.data?.data || [];

  if (!items.length)
    return (
      <>
        <PageHeader
          eyebrow="Executive"
          title="Portfolios"
          subtitle="Mission-aligned investment portfolios across DHA."
        />
        <BrandedEmptyState
          icon="🗂️"
          eyebrow="Portfolio register"
          title="No portfolios"
          description="Mission-aligned investment portfolios will appear here once they are registered in DHA PRIME."
          tone="slate"
        />
      </>
    );

  // Portfolio-wide roll-up across the loaded page for the KPI hero band.
  const totals = items.reduce(
    (acc, p) => {
      acc.active += toNum(p.deliverySummary?.activeProjects);
      acc.atRisk += toNum(p.deliverySummary?.atRiskProjects);
      acc.planned += toNum(p.financialSummary?.plannedBudgetUsdM);
      acc.obligated += toNum(p.financialSummary?.obligatedUsdM);
      return acc;
    },
    { active: 0, atRisk: 0, planned: 0, obligated: 0 },
  );
  const heroItems = [
    {
      id: 'portfolios',
      label: 'Portfolios',
      value: items.length,
      footer: `${r.data?.page?.total ?? items.length} total in register`,
    },
    {
      id: 'active',
      label: 'Active projects',
      value: totals.active,
      status: 'blue',
      statusLabel: 'In delivery',
    },
    {
      id: 'at-risk',
      label: 'At-risk projects',
      value: totals.atRisk,
      status: totals.atRisk > 0 ? 'brick' : 'sage',
      statusLabel: totals.atRisk > 0 ? 'Attention' : 'Clear',
    },
    {
      id: 'planned',
      label: 'Planned budget',
      value: format.formatCurrencyMillions(totals.planned * 1_000_000),
      footer:
        totals.obligated > 0
          ? `${format.formatCurrencyMillions(totals.obligated * 1_000_000)} obligated`
          : undefined,
    },
  ];

  return (
    <div className="pf-redesign">
      <PageHeader
        eyebrow="Executive"
        title="Portfolios"
        subtitle="Mission-aligned investment portfolios across DHA."
      />

      <KpiHeroBand eyebrow="Portfolio posture" items={heroItems} columns={4} />

      <StaggerReveal className="bento" style={{ marginTop: 16 }}>
        {items.map((p) => {
          const active = toNum(p.deliverySummary?.activeProjects);
          const atRisk = toNum(p.deliverySummary?.atRiskProjects);
          const onTrack = Math.max(0, active - atRisk);
          const planned = toNum(p.financialSummary?.plannedBudgetUsdM);
          const obligated = toNum(p.financialSummary?.obligatedUsdM);
          const hasDelivery = p.deliverySummary?.activeProjects != null || atRisk > 0;
          const segments = [
            { label: 'On track', count: onTrack, tone: 'sage' },
            { label: 'At risk', count: atRisk, tone: atRisk > 0 ? 'brick' : 'slate' },
          ];
          return (
            <Link to={`/portfolios/${p.id}`} key={p.id} className="span-4 card interactive pf-card">
              <div className="pf-card__head">
                <div>
                  <div className="pf-card__title">{p.shortName || p.name}</div>
                  {p.type ? <div className="pf-card__type">{p.type}</div> : null}
                </div>
                <HealthIndicator health={p.health?.overall} />
              </div>

              {p.description ? <p className="pf-card__desc">{p.description}</p> : null}

              <div className="pf-card__health">
                {hasDelivery ? (
                  <StatusDonutMini
                    segments={segments}
                    size={72}
                    totalLabel="Projects"
                    centerValue={active || onTrack + atRisk}
                    ariaLabel={`${p.shortName || p.name} delivery health`}
                  />
                ) : (
                  <span className="muted" style={{ fontSize: '0.78rem' }}>
                    No delivery data
                  </span>
                )}
                <div className="pf-card__metrics">
                  <div className="pf-metric">
                    <span className="pf-metric__label">Active</span>
                    <span className="pf-metric__value">{active}</span>
                  </div>
                  <div className="pf-metric">
                    <span className="pf-metric__label">At risk</span>
                    <span className={`pf-metric__value ${deliveryTone({ atRisk, active })}`}>
                      {atRisk}
                    </span>
                  </div>
                  {planned > 0 ? (
                    <div className="pf-metric">
                      <span className="pf-metric__label">Planned</span>
                      <span className="pf-metric__value">
                        {format.formatCurrencyMillions(planned * 1_000_000)}
                      </span>
                    </div>
                  ) : null}
                  {obligated > 0 ? (
                    <div className="pf-metric">
                      <span className="pf-metric__label">Obligated</span>
                      <span className="pf-metric__value">
                        {format.formatCurrencyMillions(obligated * 1_000_000)}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>

              {(p.strategicThemes || []).length ? (
                <div className="pf-card__themes">
                  {(p.strategicThemes || []).slice(0, 3).map((theme) => (
                    <span key={theme} className="chip blue sm">
                      {theme}
                    </span>
                  ))}
                </div>
              ) : null}
            </Link>
          );
        })}
      </StaggerReveal>

      <PageViewerControls
        page={r.data?.page}
        label="portfolios"
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </div>
  );
}
